import { parseDateKey, toDateKey } from './dateKey';

/**
 * School vacation breaks and closure days for the Flights calendar.
 * Inclusive dates. A Friday or Monday closure extends across the adjacent
 * weekend so the long weekend is one band. Tuesday and Wednesday closures
 * stay that single day.
 *
 * Barnstable: https://www.barnstableacademy.com/app/uploads/2026/07/Barnstable-Academy-Calendar-26-27.pdf
 * Skidmore: https://www.skidmore.edu/registrar/documents/academiccalendar2026.pdf
 *
 * Skidmore winter: fall semester ends Sat Dec 19; returning students
 * arrive Sun Jan 24, so the break is Dec 20 through Jan 23.
 */

export interface SchoolBreak {
  school: 'Barnstable' | 'Skidmore';
  name: string;
  /** Inclusive YYYY-MM-DD. */
  start: string;
  /** Inclusive YYYY-MM-DD. */
  end: string;
  sourceUrl: string;
}

const BARNSTABLE_SOURCE =
  'https://www.barnstableacademy.com/app/uploads/2026/07/Barnstable-Academy-Calendar-26-27.pdf';
const SKIDMORE_SOURCE = 'https://www.skidmore.edu/registrar/documents/academiccalendar2026.pdf';

export const SCHOOL_BREAKS: readonly SchoolBreak[] = [
  {
    school: 'Barnstable',
    name: 'Thanksgiving Recess',
    start: '2026-11-26',
    end: '2026-11-29',
    sourceUrl: BARNSTABLE_SOURCE,
  },
  {
    school: 'Barnstable',
    name: 'Winter Recess',
    start: '2026-12-24',
    end: '2027-01-03',
    sourceUrl: BARNSTABLE_SOURCE,
  },
  {
    school: 'Barnstable',
    name: "Presidents' Week Recess",
    start: '2027-02-13',
    end: '2027-02-21',
    sourceUrl: BARNSTABLE_SOURCE,
  },
  {
    school: 'Barnstable',
    name: 'Spring Break',
    start: '2027-04-17',
    end: '2027-04-25',
    sourceUrl: BARNSTABLE_SOURCE,
  },
  // Barnstable 2026-27 closures. Source:
  // https://www.barnstableacademy.com/app/uploads/2026/07/Barnstable-Academy-Calendar-26-27.pdf
  {
    school: 'Barnstable',
    name: 'Labor Day',
    start: '2026-09-05',
    end: '2026-09-07',
    sourceUrl: BARNSTABLE_SOURCE,
  },
  {
    school: 'Barnstable',
    name: 'Yom Kippur',
    start: '2026-09-19',
    end: '2026-09-21',
    sourceUrl: BARNSTABLE_SOURCE,
  },
  {
    school: 'Barnstable',
    name: "Indigenous Peoples' Day",
    start: '2026-10-10',
    end: '2026-10-12',
    sourceUrl: BARNSTABLE_SOURCE,
  },
  {
    school: 'Barnstable',
    name: 'Election Day (no school for students)',
    start: '2026-11-03',
    end: '2026-11-03',
    sourceUrl: BARNSTABLE_SOURCE,
  },
  {
    school: 'Barnstable',
    name: 'Veterans Day',
    start: '2026-11-11',
    end: '2026-11-11',
    sourceUrl: BARNSTABLE_SOURCE,
  },
  {
    school: 'Barnstable',
    name: 'Martin Luther King Jr. Day',
    start: '2027-01-16',
    end: '2027-01-18',
    sourceUrl: BARNSTABLE_SOURCE,
  },
  {
    school: 'Barnstable',
    name: 'Professional Development & Head of School Holiday',
    start: '2027-03-12',
    end: '2027-03-15',
    sourceUrl: BARNSTABLE_SOURCE,
  },
  {
    school: 'Barnstable',
    name: 'Good Friday',
    start: '2027-03-26',
    end: '2027-03-28',
    sourceUrl: BARNSTABLE_SOURCE,
  },
  {
    school: 'Barnstable',
    name: 'Memorial Day weekend',
    start: '2027-05-28',
    end: '2027-05-31',
    sourceUrl: BARNSTABLE_SOURCE,
  },
  // Skidmore 2026-27 closure. Source:
  // https://www.skidmore.edu/registrar/documents/academiccalendar2026.pdf
  {
    school: 'Skidmore',
    name: 'Study Day (no classes)',
    start: '2026-10-10',
    end: '2026-10-12',
    sourceUrl: SKIDMORE_SOURCE,
  },
  {
    school: 'Skidmore',
    name: 'Thanksgiving Vacation',
    start: '2026-11-25',
    end: '2026-11-29',
    sourceUrl: SKIDMORE_SOURCE,
  },
  {
    school: 'Skidmore',
    name: 'Winter Break',
    start: '2026-12-20',
    end: '2027-01-23',
    sourceUrl: SKIDMORE_SOURCE,
  },
  {
    school: 'Skidmore',
    name: 'Spring Vacation',
    start: '2027-03-13',
    end: '2027-03-21',
    sourceUrl: SKIDMORE_SOURCE,
  },
];

/** Breaks whose inclusive range covers `dateKey`, in list order. */
export function schoolBreaksOn(dateKey: string): SchoolBreak[] {
  return SCHOOL_BREAKS.filter((b) => dateKey >= b.start && dateKey <= b.end);
}

/** Fixed lane order. Barnstable is always the upper band, Skidmore the lower. */
const LANE_SCHOOLS = ['Barnstable', 'Skidmore'] as const;

export interface SchoolBreakLane {
  id: 'barnstable' | 'skidmore';
  school: 'Barnstable' | 'Skidmore';
  /** Band continues into the previous cell of this week row. */
  connectLeft: boolean;
  /** Band continues into the next cell of this week row. */
  connectRight: boolean;
  /** Rounded cap. This date is the first day of the break. */
  roundLeft: boolean;
  /** Rounded cap. This date is the last day of the break. */
  roundRight: boolean;
}

function shiftDateKey(dateKey: string, days: number): string {
  const date = parseDateKey(dateKey);
  date.setDate(date.getDate() + days);
  return toDateKey(date);
}

/**
 * One lane per school on break that day, Barnstable then Skidmore.
 * A lane connects across the week row only when the next cell is the same
 * school still on break. Rounded caps are the real first and last day.
 */
export function schoolBreakLanes(dateKey: string): SchoolBreakLane[] {
  const present = new Set(schoolBreaksOn(dateKey).map((b) => b.school));
  if (present.size === 0) return [];

  const prevKey = shiftDateKey(dateKey, -1);
  const nextKey = shiftDateKey(dateKey, 1);
  const prev = new Set(schoolBreaksOn(prevKey).map((b) => b.school));
  const next = new Set(schoolBreaksOn(nextKey).map((b) => b.school));
  const dow = parseDateKey(dateKey).getDay();
  const prevInRow = dow !== 0 && prevKey.slice(0, 7) === dateKey.slice(0, 7);
  const nextInRow = dow !== 6 && nextKey.slice(0, 7) === dateKey.slice(0, 7);

  const lanes: SchoolBreakLane[] = [];
  for (const school of LANE_SCHOOLS) {
    if (!present.has(school)) continue;
    const continuesPrev = prev.has(school);
    const continuesNext = next.has(school);
    lanes.push({
      id: school === 'Barnstable' ? 'barnstable' : 'skidmore',
      school,
      connectLeft: continuesPrev && prevInRow,
      connectRight: continuesNext && nextInRow,
      roundLeft: !continuesPrev,
      roundRight: !continuesNext,
    });
  }
  return lanes;
}

/** "Barnstable Winter Recess" — school, then the published break name. */
export function schoolBreakLabel(b: SchoolBreak): string {
  return `${b.school} ${b.name}`;
}

function formatBreakDay(iso: string): string {
  return parseDateKey(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Full inclusive range, e.g. "Nov 26, 2026 – Nov 29, 2026". */
export function schoolBreakDateRange(b: SchoolBreak): string {
  return `${formatBreakDay(b.start)} – ${formatBreakDay(b.end)}`;
}
